import React, { useEffect } from "react";
import { Outlet, useNavigate, useLocation } from "react-router-dom";
import { Layout, Menu, Badge, Avatar, Dropdown, Button, message } from "antd";
import { useQuery } from "@tanstack/react-query";
import {
  WalletOutlined,
  StarOutlined,
  RobotOutlined,
  FilterOutlined,
  BellOutlined,
  UserOutlined,
  LogoutOutlined,
  MenuFoldOutlined,
  MenuUnfoldOutlined,
} from "@ant-design/icons";
import { useAuthStore } from "../stores/authStore";
import { notificationApi } from "../services/notificationApi";
import { siteBrandingApi } from "../services/siteBrandingApi";
import authApi from "../services/authApi";
import type { MenuProps } from "antd";

const { Header, Sider, Content } = Layout;
const DEFAULT_BRAND_LOGO_URL = "/apple-touch-icon.png";

type MenuItem = Required<MenuProps>["items"][number];

const MainLayout: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthenticated, logout, user, token, setUser } = useAuthStore();
  const hasBetaRole = (user?.roles || []).includes("beta_user");
  const [collapsed, setCollapsed] = React.useState(false);
  const [isImpersonation, setIsImpersonation] = React.useState(false);
  const { data: unreadCount = 0 } = useQuery({
    queryKey: ["notification-unread-count"],
    queryFn: () => notificationApi.getUnreadCount(),
  });
  const { data: branding } = useQuery({
    queryKey: ["site-branding"],
    queryFn: () => siteBrandingApi.getSiteBranding(),
    staleTime: 10 * 60 * 1000,
  });

  useEffect(() => {
    document.title = branding?.system_name || "野牛AI";
  }, [branding?.system_name]);

  useEffect(() => {
    if (!isAuthenticated) {
      const redirect = `${location.pathname}${location.search}`;
      navigate(`/login?redirect=${encodeURIComponent(redirect)}`);
    }
  }, [isAuthenticated, location.pathname, location.search, navigate]);

  useEffect(() => {
    if (!isAuthenticated || !token) {
      return;
    }
    const syncUser = async () => {
      try {
        const latestUser = await authApi.getCurrentUser();
        setUser(latestUser);
      } catch (error) {
        console.error("Failed to refresh current user info:", error);
      }
    };
    void syncUser();
  }, [isAuthenticated, token, setUser]);

  useEffect(() => {
    const root = document.documentElement;
    if (!token) {
      root.classList.remove("impersonation-mode");
      setIsImpersonation(false);
      return;
    }
    try {
      const part = token.split(".")[1] || "";
      const normalized = part.replace(/-/g, "+").replace(/_/g, "/");
      const payload = JSON.parse(atob(normalized));
      if (payload?.imp === true) {
        root.classList.add("impersonation-mode");
        setIsImpersonation(true);
      } else {
        root.classList.remove("impersonation-mode");
        setIsImpersonation(false);
      }
    } catch {
      root.classList.remove("impersonation-mode");
      setIsImpersonation(false);
    }
  }, [token]);

  const menuItems: MenuItem[] = [
    {
      key: "/holdings",
      icon: <WalletOutlined />,
      label: "虚拟账户",
    },
    {
      key: "/watchlist",
      icon: <StarOutlined />,
      label: "自选股",
    },
    {
      key: "/analysis",
      icon: <RobotOutlined />,
      label: "AI分析",
    },
    ...(hasBetaRole
      ? [{
          key: "/stock-screening",
          icon: <FilterOutlined />,
          label: "条件选股",
        } as MenuItem]
      : []),
    {
      key: "/profile",
      icon: <UserOutlined />,
      label: "个人中心",
    },
  ];

  const handleMenuClick = (key: string) => {
    navigate(key);
  };

  const handleLogout = () => {
    logout();
    message.success("已退出登录");
    navigate("/login");
  };

  const userMenuItems: MenuItem[] = [
    {
      key: "profile",
      icon: <UserOutlined />,
      label: "个人中心",
      onClick: () => navigate("/profile"),
    },
    {
      key: "settings",
      icon: <UserOutlined />,
      label: "系统设置",
      onClick: () => navigate("/profile?tab=security"),
    },
    ...(isImpersonation
      ? ([
          {
            type: "divider",
          },
          {
            key: "exit-impersonation",
            icon: <LogoutOutlined />,
            label: "退出模拟",
            onClick: () => {
              logout();
              message.success("已退出模拟");
              navigate("/login");
            },
          },
        ] as MenuItem[])
      : []),
    {
      type: "divider",
    },
    {
      key: "logout",
      icon: <LogoutOutlined />,
      label: "退出登录",
      onClick: handleLogout,
    },
  ];

  const selectedKey = location.pathname;

  return (
    <Layout style={{ height: "100vh", overflow: "hidden" }}>
      <Sider
        trigger={null}
        collapsible
        collapsed={collapsed}
        theme="light"
        style={{
          height: "100vh",
          overflow: "hidden",
          boxShadow: "2px 0 8px 0 rgba(29,35,41,0.05)",
          display: "flex",
          flexDirection: "column",
        }}
      >
        <div
          style={{
            height: 64,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            borderBottom: "1px solid #f0f0f0",
          }}
        >
          <div
            style={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 10,
              minWidth: 0,
            }}
          >
            <img
              src={DEFAULT_BRAND_LOGO_URL}
              alt={branding?.system_name || "系统 Logo"}
              style={{
                width: 34,
                height: 34,
                borderRadius: 8,
                objectFit: "contain",
                flex: "0 0 auto",
              }}
            />
            {!collapsed ? (
              <h2
                style={{
                  margin: 0,
                  minWidth: 0,
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                  fontSize: 20,
                  fontWeight: 600,
                }}
              >
                {branding?.system_name || "投研系统"}
              </h2>
            ) : null}
          </div>
        </div>
        <Menu
          mode="inline"
          selectedKeys={[selectedKey]}
          items={menuItems}
          onClick={({ key }) => handleMenuClick(key)}
          style={{ borderRight: 0, flex: 1, overflowY: "auto", minHeight: 0 }}
        />
      </Sider>
      <Layout style={{ minWidth: 0, height: "100vh", overflow: "hidden" }}>
        <Header
          style={{
            flex: "0 0 64px",
            background: "#fff",
            padding: "0 24px",
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            boxShadow: "0 1px 4px rgba(0,21,41,0.08)",
            zIndex: 10,
          }}
        >
          <Button
            type="text"
            icon={collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
            onClick={() => setCollapsed(!collapsed)}
          />
          <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
            <Badge count={unreadCount} size="small">
              <Button
                type="text"
                icon={<BellOutlined />}
                onClick={() => navigate("/notifications")}
              />
            </Badge>
            <Dropdown menu={{ items: userMenuItems }} placement="bottomRight">
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 8,
                  cursor: "pointer",
                }}
              >
                <Avatar icon={<UserOutlined />} src={user?.avatar_url} />
                <span style={{ display: collapsed ? "none" : "inline" }}>
                  {user?.nickname || user?.phone || "用户"}
                </span>
              </div>
            </Dropdown>
          </div>
        </Header>
        <Content
          style={{
            flex: 1,
            minHeight: 0,
            margin: 24,
            marginBottom: 8,
            padding: 24,
            background: "#fff",
            borderRadius: 8,
            overflowY: "auto",
            overflowX: "hidden",
          }}
        >
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
